import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isToday,
  isSameMonth,
} from "date-fns"
import { ru } from "date-fns/locale"
import { useMemo, useState } from "react"
import { useCalendarEventStore } from "@/store/calendarEventStore"
import { usePersonalRemindersStore } from "@/store/personalRemindersStore"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { Bell } from "lucide-react"

interface MonthCalendarProps {
  date: Date
  isSolo?: boolean
  onDayClick?: (date: Date) => void
  filterType?: "all" | "task" | "holiday" | "meeting" | "local-emergency"
}

export function MonthCalendar({
  date,
  isSolo = false,
  onDayClick,
  filterType = "all",
}: MonthCalendarProps) {
  const { events } = useCalendarEventStore()
  const { reminders } = usePersonalRemindersStore()
  const [hoveredDate, setHoveredDate] = useState<string | null>(null)

  const monthStart = startOfMonth(date)
  const monthEnd = endOfMonth(date)
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 })
  const endDate = endOfWeek(monthEnd)

  const days = eachDayOfInterval({ start: startDate, end: endDate })

  const daysOfWeek = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"]

  // === ИЗМЕНЁННЫЙ БЛОК: учитываем все типы праздников ===
  const eventsByDate = useMemo(() => {
    const map: Record<string, {
      task: number;
      holiday: number;
      meeting: number;
      "local-emergency": number;
    }> = {};

    events.forEach((ev) => {
      if (filterType !== "all" && ev.type !== filterType) return;

      if (!map[ev.date]) {
        map[ev.date] = { task: 0, holiday: 0, meeting: 0, "local-emergency": 0 };
      }

      if (ev.type === "task") {
        map[ev.date].task++;
      } else if (
        ev.type === "holiday" ||
        ev.type === "leader_birthday" ||
        ev.type === "leader_appointment" ||
        ev.type === "branch_opening"
      ) {
        map[ev.date].holiday++;        // Все годовщины и праздники — в одну зелёную категорию
      } else if (ev.type === "meeting") {
        map[ev.date].meeting++;
      } else if (ev.type === "local-emergency") {
        map[ev.date]["local-emergency"]++;
      }
    });

    return map;
  }, [events, filterType]);

  const remindersByDate = useMemo(() => {
    const map: Record<string, number> = {};
    reminders.forEach((rem) => {
      if (!map[rem.date]) map[rem.date] = 0;
      map[rem.date]++;
    });
    return map;
  }, [reminders]);

  const baseCellSize = isSolo ? "min-h-[3.8rem] min-w-[3.8rem]" : "min-h-[3.2rem] min-w-[3.2rem]";
  const baseFontSize = isSolo ? "text-xl" : "text-base";
  const titleSize = isSolo ? "text-2xl" : "text-xl";

  return (
    <div
      className={`
        bg-gradient-to-br from-purple-950/80 to-indigo-950/80 
        p-4 sm:p-5 md:p-6 
        rounded-3xl border border-purple-700/50 
        shadow-2xl shadow-purple-900/40
        w-full
      `}
    >
      <h3
        className={`
          ${titleSize} font-bold text-white mb-4 sm:mb-5 md:mb-6 
          text-center bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent
        `}
      >
        {format(date, "LLLL yyyy", { locale: ru })}
      </h3>

      <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
        {daysOfWeek.map((day) => (
          <div
            key={day}
            className={`
              text-center text-purple-300 font-medium 
              ${isSolo ? "text-base" : "text-sm sm:text-base"}
            `}
          >
            {day}
          </div>
        ))}

        {days.map((day) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const dayEvents = eventsByDate[dateStr] || { task: 0, holiday: 0, meeting: 0, "local-emergency": 0 };
          const total = dayEvents.task + dayEvents.holiday + dayEvents.meeting + dayEvents["local-emergency"];
          const reminderCount = remindersByDate[dateStr] || 0;
          const isCurrentMonth = isSameMonth(day, monthStart);
          const isCurrentDay = isToday(day);

          const hoveredEvents = events
            .filter((ev) => ev.date === dateStr && (filterType === "all" || ev.type === filterType))
            .slice(0, 3);

          return (
            <Popover
              key={dateStr}
              open={hoveredDate === dateStr}
              onOpenChange={(open) => !open && setHoveredDate(null)}
            >
              <PopoverTrigger asChild>
                <div
                  onClick={() => isCurrentMonth && onDayClick?.(day)}
                  onMouseEnter={() => (total > 0 || reminderCount > 0) && setHoveredDate(dateStr)}
                  onMouseLeave={() => setHoveredDate(null)}
                  data-today={isCurrentDay}
                  className={cn(
                    "relative flex flex-col items-center justify-between rounded-xl transition-all duration-300 cursor-pointer",
                    baseCellSize,
                    isCurrentMonth
                      ? isCurrentDay
                        ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold shadow-lg scale-105 ring-2 ring-purple-400/50"
                        : "text-white hover:bg-purple-700/50 hover:scale-105"
                      : "text-purple-500/50 cursor-default",
                    baseFontSize,
                    "font-medium"
                  )}
                >
                  <span className="mt-1">{format(day, "d")}</span>

                  {/* Цветные точки событий */}
                  {total > 0 && (
                    <div className="flex gap-1 pb-1 mt-auto">
                      {dayEvents.task > 0 && (
                        <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-red-500 to-rose-600 shadow-sm" />
                      )}
                      {dayEvents.holiday > 0 && (
                        <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 shadow-sm" />
                      )}
                      {dayEvents.meeting > 0 && (
                        <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-blue-500 to-cyan-600 shadow-sm" />
                      )}
                      {dayEvents["local-emergency"] > 0 && (
                        <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 shadow-sm" />
                      )}
                      {total > 3 && (
                        <div className="w-2.5 h-2.5 rounded-full bg-purple-500/70 flex items-center justify-center text-[8px] text-white font-bold">
                          +{total - 3}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Маркер напоминаний */}
                  {reminderCount > 0 && (
                    <div className="absolute bottom-1 right-1">
                      <Bell className="h-4 w-4 text-orange-400 animate-pulse" />
                      {reminderCount > 1 && (
                        <span className="absolute -top-1 -right-1 bg-orange-700 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-1 shadow-sm">
                          {reminderCount}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </PopoverTrigger>

              {total > 0 && (
                <PopoverContent className="w-64 bg-purple-950 border-purple-700/50 text-white p-3 shadow-2xl">
                  <div className="space-y-2">
                    {hoveredEvents.map((ev) => (
                      <div key={ev.id} className="text-sm">
                        <span
                          className={cn(
                            "font-medium",
                            ev.type === "task" ? "text-red-400" :
                            ev.type === "holiday" || 
                            ev.type === "leader_birthday" || 
                            ev.type === "leader_appointment" || 
                            ev.type === "branch_opening" ? "text-emerald-400" :
                            ev.type === "meeting" ? "text-blue-400" :
                            ev.type === "local-emergency" ? "text-orange-400" :
                            "text-purple-400"
                          )}
                        >
                          {ev.title}
                        </span>
                        {ev.description && (
                          <p className="text-purple-300 text-xs mt-0.5">{ev.description}</p>
                        )}
                      </div>
                    ))}
                    {total > 3 && (
                      <p className="text-xs text-purple-400 mt-1">+ ещё {total - 3} событий</p>
                    )}
                  </div>
                </PopoverContent>
              )}
            </Popover>
          );
        })}
      </div>
    </div>
  );
}